import { z } from "zod";
import i18n from "@/i18n";
import { resolvePrintLayout, sanitizeHeaderHtml, type RawPrintSettings } from "@vet/print-core";
import type { PrintSettings, PrintSettingsInput } from "@/hooks/usePrintSettings";
import { getContrastRatio, MIN_PRINT_CONTRAST } from "./contrast";

export type PrintScope = "prescription" | "term";
export type ImagePreviews = { background: string | null; header: string | null; footer: string | null };

const t = (key: string, opts?: Record<string, unknown>) => i18n.t(key, { ns: "validation", ...opts });
const margin = (min: number, max: number) =>
  z
    .number({ invalid_type_error: t("printNumberRequired"), required_error: t("printNumberRequired") })
    .min(min, { message: t("printMarginRange", { min, max }) })
    .max(max, { message: t("printMarginRange", { min, max }) });
const hex = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, { message: t("printColorInvalid") })
  .refine((v) => getContrastRatio(v, "#FFFFFF") >= MIN_PRINT_CONTRAST, { message: t("printColorLowContrast") });

export const printLayoutFormSchema = z.object({
  paper: z.enum(["A4", "A5", "Letter"]),
  marginTopMm: margin(5, 40),
  marginSideMm: margin(5, 40),
  marginBottomMm: margin(8, 40),
  backgroundImageUrl: z.string(),
  headerLayout: z.enum(["inline", "inline_apart", "center", "left", "right", "full"]),
  headerImageUrl: z.string(),
  headerLogoHeightPx: z.number().min(32).max(120),
  headerHtml: z.string().max(4000, { message: t("printHeaderTooLong") }),
  footerText: z.string().max(240, { message: t("printFooterTooLong") }),
  footerAlign: z.enum(["left", "center", "right"]),
  fontId: z.string().min(1),
  fontSizePt: z.number().min(9).max(12),
  lineHeight: z.union([z.literal(1.15), z.literal(1.5), z.literal(2)]),
  textColor: hex,
  titleColor: hex,
  headerTextColor: hex,
  footerTextColor: hex,
  showIssuerBlock: z.boolean(),
  stampShowCrmv: z.boolean(),
  stampShowMapa: z.boolean(),
});
export type PrintLayoutFormValues = z.infer<typeof printLayoutFormSchema>;

export function formValuesFromSettings(
  settings: PrintSettings | null | undefined,
  scope: PrintScope,
): PrintLayoutFormValues {
  const l = resolvePrintLayout(settings as unknown as RawPrintSettings, scope);
  return {
    paper: l.paper,
    marginTopMm: l.marginTopMm,
    marginSideMm: l.marginSideMm,
    marginBottomMm: l.marginBottomMm,
    backgroundImageUrl: l.backgroundImageUrl ?? "",
    headerLayout: l.headerLayout,
    headerImageUrl: l.headerImageUrl ?? "",
    headerLogoHeightPx: l.headerLogoHeightPx,
    headerHtml: l.headerHtml,
    footerText: l.footerText ?? "",
    footerAlign: l.footerAlign,
    fontId: l.fontId,
    fontSizePt: l.fontSizePt,
    lineHeight: l.lineHeight as 1.15 | 1.5 | 2,
    textColor: l.textColor,
    titleColor: l.titleColor,
    headerTextColor: l.headerTextColor,
    footerTextColor: l.footerTextColor,
    showIssuerBlock: l.showIssuerBlock,
    stampShowCrmv: l.stampShowCrmv,
    stampShowMapa: l.stampShowMapa,
  };
}

export function settingsPatchFromForm(v: PrintLayoutFormValues, scope: PrintScope): PrintSettingsInput {
  const p = scope === "term" ? "term_" : "";
  const empty = (s: string) => (s.trim() ? s.trim() : null);
  return {
    [`${p}paper_size`]: v.paper,
    [`${p}margin_top_mm`]: v.marginTopMm,
    [`${p}margin_side_mm`]: v.marginSideMm,
    [`${p}margin_bottom_mm`]: v.marginBottomMm,
    [`${p}background_image_url`]: empty(v.backgroundImageUrl),
    [`${p}header_layout`]: v.headerLayout,
    [`${p}header_image_url`]: empty(v.headerImageUrl),
    [`${p}header_logo_height_px`]: v.headerLogoHeightPx,
    [`${p}header_text`]: empty(sanitizeHeaderHtml(v.headerHtml)),
    [`${p}footer_text`]: empty(v.footerText),
    [`${p}footer_image_url`]: null,
    [`${p}footer_layout`]: v.footerAlign,
    [`${p}text_font_family`]: v.fontId,
    [`${p}font_size_pt`]: v.fontSizePt,
    [`${p}line_height`]: v.lineHeight,
    [`${p}text_color`]: v.textColor,
    [`${p}title_color`]: v.titleColor,
    [`${p}header_text_color`]: v.headerTextColor,
    [`${p}footer_text_color`]: v.footerTextColor,
    [`${p}show_issuer_block`]: v.showIssuerBlock,
    [`${p}stamp_show_crmv`]: v.stampShowCrmv,
    [`${p}stamp_show_mapa`]: v.stampShowMapa,
  } as PrintSettingsInput;
}

export function previewSettingsFromForm(
  v: PrintLayoutFormValues,
  scope: PrintScope,
  previews: ImagePreviews,
): Partial<PrintSettings> {
  const p = scope === "term" ? "term_" : "";
  const patch = settingsPatchFromForm(v, scope) as Record<string, unknown>;
  if (previews.background) patch[`${p}background_image_url`] = previews.background;
  if (previews.header) patch[`${p}header_image_url`] = previews.header;
  return patch as Partial<PrintSettings>;
}
